import { getNodeTypeName } from 'utils/names';

import type { CandidateRelationView, NodeMap, RelationMap } from '../types';
import { relationLabel } from './relationLabel';

/**
 * Enumerate the candidate relations for a focused node from the project template. Relations are stored
 * on their *source* node, so:
 *  - **forward** candidates are the relations defined on the focused type (it is the source); the
 *    connected type is `relation.targetNodeId` and the label comes from `displayNames`.
 *  - **reverse** candidates are relations on *other* types whose `targetNodeId` is the focused type;
 *    the connected type is that other (source) type and the label comes from `reverseDisplayNames`.
 *
 * A self-referential relation legitimately yields both a forward and a reverse candidate (distinct keys).
 * A connected type absent from `nodeMap` (hidden by the user's blueprint, or dangling) is skipped.
 */
export function candidateRelations(
  focusedTemplateNodeId: string,
  nodeMap: NodeMap,
  relationMap: RelationMap
): CandidateRelationView[] {
  const candidates: CandidateRelationView[] = [];

  for (const { relation, sourceTemplateNodeId } of Object.values(relationMap)) {
    const isForward = sourceTemplateNodeId === focusedTemplateNodeId;
    const isReverse = relation.targetNodeId === focusedTemplateNodeId;
    if (!isForward && !isReverse) continue;

    const addCandidate = (reverse: boolean) => {
      const connectedTemplateNodeId = reverse ? sourceTemplateNodeId : relation.targetNodeId;
      const connectedNode = nodeMap[connectedTemplateNodeId];
      if (!connectedNode) return; // type hidden by the user's blueprint, or dangling relation

      candidates.push({
        key: `${relation.id}__${connectedTemplateNodeId}__${reverse ? 'r' : 'f'}`,
        relationId: relation.id,
        isReverse: reverse,
        label: relationLabel(relation, reverse),
        connectedType: {
          templateNodeId: connectedTemplateNodeId,
          technicalName: connectedNode.technicalName,
          displayName: getNodeTypeName(connectedNode),
          icon: connectedNode.icon || 'Node'
        }
      });
    };

    if (isForward) addCandidate(false);
    if (isReverse) addCandidate(true);
  }

  return candidates;
}
